// @file Surface AI Talk store.
import { AUTODETECT_VOICE, DEFAULT_ENGLISH_VOICE, EXAMPLES, voices } from '@@/bits/ai_talk_helper'
import { trackEvent } from '@@/bits/analytics'
import { currentCountryIso31662 } from '@@/bits/current_locale'
import { captureFetchException } from '@@/bits/error_tracker'
import window from '@@/bits/global'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { AiTalkApi } from '@@/surface/api/ai_talk'
import type { Voice } from '@@/types'
import { HTTPAuthenticationScheme } from '@padlet/fetch'
import { debounce, isEqual } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum AiTalkStatus {
  Initial = 'Initial',
  Generating = 'Generating',
  Generated = 'Generated',
  Errored = 'Errored',
}
const TEXT_LIMIT = 1000
const DISPLAY_TEXT_LIMIT = 2000 // This is the limit for the display of the text in the input

export const useSurfaceAiTalkStore = defineStore('surfaceAiTalk', () => {
  // State variables
  const text = ref('')
  const status = ref<AiTalkStatus>(AiTalkStatus.Initial)
  const audioUrl = ref('')
  const voiceSelected = ref<Voice>(AUTODETECT_VOICE)
  const hasManuallySelectedVoice = ref(false)
  const currentSampleIndex = ref(-1)
  let abortController: AbortController | null = null

  // add fallback for use cases outside of surface
  // outside of surface, need to define morpheusToken in vueStartingState.constants (see add_post_controller.rb for reference)
  const morpheusToken = computed(
    () => useSurfaceStartingStateStore().morpheusToken ?? window.ww?.vueStartingState?.constants?.morpheusToken,
  )

  // Computed properties
  const trimmedText = computed(() => text.value.trim())
  const rawTextLength = computed(() => text.value.length)
  const isInputOnlySpaces = computed(() => trimmedText.value.length === 0)
  const isInputValid = computed(
    () => !isInputOnlySpaces.value && rawTextLength.value <= TEXT_LIMIT && trimmedText.value.length > 0,
  )
  const isGenerating = computed(() => status.value === AiTalkStatus.Generating)
  const isErrored = computed(() => status.value === AiTalkStatus.Errored)
  const isGenerated = computed(() => status.value === AiTalkStatus.Generated)
  const languageSelected = computed(() => voiceSelected.value.languageName)

  const defaultCountryCode = computed(() => {
    const countryCode = useSurfaceStartingStateStore().currentCountryCode ?? currentCountryIso31662
    const matchingVoice = voices.find((voice) => voice.languageCode === countryCode)
    if (matchingVoice != null) {
      return countryCode
    }
    // Defaults to en-US if the country code is not found in the list of voices or if the country code is not set
    return 'en-US'
  })

  const currentCountryCode = computed(() =>
    hasManuallySelectedVoice.value ? voiceSelected.value.languageCode : defaultCountryCode.value,
  )

  const isSampleTextSubmitted = computed<boolean>(() => {
    return EXAMPLES[currentCountryCode.value].includes(trimmedText.value)
  })

  // Language detection
  async function getLanguageCodeFromTextAndSet(): Promise<void> {
    try {
      const response = await AiTalkApi.getLanguage(trimmedText.value, {
        authorization: [HTTPAuthenticationScheme.bearer, morpheusToken.value],
      })
      // Set default English to en-US
      if (response.languageCode === 'en') {
        response.languageCode = 'en-US'
      }

      // Explicitly handle the case where the language code is zh-CN, zh-TW, etc, since Google's TTS API
      // does not support zh-XX language code. Fallback to 'cmn-CN'
      // Docs: https://cloud.google.com/text-to-speech/docs/voices
      if (response.languageCode.startsWith('zh')) {
        const matchingVoice = voices.find((voice) => voice.languageCode.endsWith(response.languageCode.split('-')[1]))

        if (matchingVoice != null) {
          voiceSelected.value = matchingVoice
        } else {
          const cmnVoice = voices.find((voice) => voice.languageCode === 'cmn-CN')
          if (cmnVoice != null) {
            voiceSelected.value = cmnVoice
          }
        }
      } else {
        const exactMatch = voices.find((voice) => voice.languageCode === response.languageCode)
        if (exactMatch != null) {
          voiceSelected.value = exactMatch
        } else {
          const prefixMatch = voices.find((voice) => voice.languageCode.startsWith(response.languageCode.split('-')[0]))
          if (prefixMatch != null) {
            voiceSelected.value = prefixMatch
          }
        }
      }
    } catch (e) {
      captureFetchException(e, { source: 'AiTalkLanguageDetection' })
    }
  }

  const debouncedLanguageDetection = debounce(() => {
    void getLanguageCodeFromTextAndSet()
  }, 1000)

  // Input handling
  function handleAiTalkTextInput(input: string): void {
    text.value = input
    if (trimmedText.value.length > 0 && isEqual(voiceSelected.value, AUTODETECT_VOICE)) {
      // If the text is not empty and the voice is autodetect or voice is not yet selected by the user, we should try to detect the language
      debouncedLanguageDetection()
    } else if (trimmedText.value.length === 0 && !hasManuallySelectedVoice.value) {
      // If the text is empty and user has not manually selected voice, we should reset the voice to autodetect
      voiceSelected.value = AUTODETECT_VOICE
    }
  }

  // Audio generation
  async function generateAudio(): Promise<void> {
    // Track what was generated for non-sample text
    if (!isSampleTextSubmitted.value) {
      trackEvent('AI Talk', 'Generate audio', null, null, { text: trimmedText.value, voice: voiceSelected.value })
    }

    // If there's an ongoing request, abort it
    if (abortController != null) {
      abortController.abort()
    }

    // Create a new AbortController for the new request
    abortController = new AbortController()
    const signal = abortController.signal

    try {
      status.value = AiTalkStatus.Generating
      if (isEqual(voiceSelected.value, AUTODETECT_VOICE)) {
        // If user submits the text before autodetection is complete, we should try to detect the language before generating the audio
        await getLanguageCodeFromTextAndSet()
        if (isEqual(voiceSelected.value, AUTODETECT_VOICE)) {
          voiceSelected.value = DEFAULT_ENGLISH_VOICE
        }
      }
      const response = await AiTalkApi.generateAudio(
        trimmedText.value,
        voiceSelected.value.languageCode,
        voiceSelected.value.name,
        {
          authorization: [HTTPAuthenticationScheme.bearer, morpheusToken.value],
          signal,
        },
      )

      status.value = AiTalkStatus.Generated
      audioUrl.value = response.url
    } catch (e) {
      if (e.name === 'AbortError') {
        status.value = AiTalkStatus.Initial
      } else {
        status.value = AiTalkStatus.Errored
        captureFetchException(e, { source: 'AiTalk' })
      }
    } finally {
      // Clean up the abort controller
      abortController = null
    }
  }

  function abortGenerateAudio(): void {
    if (abortController != null) {
      abortController.abort()
    }
  }

  // General state management
  function handleBack(): void {
    status.value = AiTalkStatus.Initial
  }

  function handleCancel(): void {
    abortGenerateAudio()
    status.value = AiTalkStatus.Initial
  }

  function generateSampleText(): void {
    const examplesLength = EXAMPLES[currentCountryCode.value].length
    currentSampleIndex.value = (currentSampleIndex.value + 1) % examplesLength
    text.value = EXAMPLES[currentCountryCode.value][currentSampleIndex.value]
    voiceSelected.value = voices.find((voice) => voice.languageCode === currentCountryCode.value) ?? AUTODETECT_VOICE
  }

  function updateVoiceSelected(voice: Voice): void {
    voiceSelected.value = voice
    // If user manually selects a voice, we should remember that
    if (!isEqual(voice, AUTODETECT_VOICE)) {
      hasManuallySelectedVoice.value = true
    } else {
      // If user selects autodetect, we should remember that as well so that we can reset the voice when the text is empty
      hasManuallySelectedVoice.value = false
    }
  }

  function reset(): void {
    text.value = ''
    status.value = AiTalkStatus.Initial
    audioUrl.value = ''
    voiceSelected.value = AUTODETECT_VOICE
    hasManuallySelectedVoice.value = false
    currentSampleIndex.value = -1
    if (abortController != null) {
      abortController.abort()
      abortController = null
    }
  }

  return {
    // State
    text,
    status,
    audioUrl,
    voiceSelected,
    hasManuallySelectedVoice,
    currentSampleIndex,

    // Computed
    trimmedText,
    rawTextLength,
    isInputOnlySpaces,
    isInputValid,
    isGenerating,
    isErrored,
    isGenerated,
    languageSelected,
    defaultCountryCode,
    currentCountryCode,
    isSampleTextSubmitted,

    // Methods
    handleAiTalkTextInput,
    generateAudio,
    abortGenerateAudio,
    handleBack,
    handleCancel,
    generateSampleText,
    updateVoiceSelected,
    getLanguageCodeFromTextAndSet,
    reset,
  }
})

export { AiTalkStatus, DISPLAY_TEXT_LIMIT, TEXT_LIMIT }
